
import React, { useEffect, useMemo, useState } from 'react';
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import { URLS } from "@/lib/urls";
import { useApiSearchTeacherTimes } from "@/api/teacherTimes";
import TableSearch from "@/components/table/tableSearch";
import { convertToJalaliWithFormat } from "@/lib/utils/date";
import toFaNumber, { toFaNumberFormat } from "@/lib/utils/number";
import { TeacherTimesStatusEnum, TeacherTimesTypeEnum } from "@/lib/helper/enumType";
import { Link } from "react-router-dom";
import { sprintf } from "@/lib/utils/sprintf";
import TimeSummary from "@/pages/teacher/times/_section/TimeSummary";
import { useApiSearchTeacherLessons } from "@/api/lesson";
import dayjs from "dayjs";
import RenderLoading from "@/components/render-if-loading";
import { Button, DatePicker, Divider, Modal, Select, Tag } from "antd";
import { CalendarDays, Eye, Filter } from "lucide-react";

function TeacherTimesListPage(props) {
    const [openModal, setOpenModal] = useState(false);

    const [args, setArgs] = useState({
        from_date: undefined,
        to_date: undefined,
        lesson_id: undefined
    });

    const { data: lessonList, isPending: isPendingLessonList } =
        useApiSearchTeacherLessons({ per_page: 1000 });

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [lesson, setLesson] = useState();

    useEffect(() => {
        setArgs({
            ...args,
            from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
            to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
            lesson_id: lesson
        });
    }, [fromDate, toDate, lesson]);

    const columns = useMemo(() => [
        {
            title: 'درس',
            dataIndex: 'lesson_id',
            key: 'lesson_id',
            render: (text, record) => (
                <Link
                    to={sprintf(URLS.teacher.lessons.detail, { id: record?.lesson_id })}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                    <div className="flex flex-col">
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                            {record?.lesson?.code}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {record?.topic?.title}
                        </span>
                    </div>
                </Link>
            )
        },
        {
            title: 'تاریخ',
            dataIndex: 'date',
            key: 'date',
            render: (text) => (
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <CalendarDays size={15} className="text-gray-500 dark:text-gray-400" />
                    {convertToJalaliWithFormat(text)}
                </div>
            )
        },
        {
            title: 'زمان فعالیت',
            dataIndex: 'totaltime',
            key: 'totaltime',
            render: (text) => (
                <Tag color="orange">
                    {text}
                </Tag>
            )
        },
        {
            title: 'مبلغ',
            dataIndex: 'finalprice',
            key: 'finalprice',
            render: (text) => (
                <div className="font-bold text-emerald-700 dark:text-emerald-400">
                    {toFaNumberFormat(text)}
                    <span className="text-xs mr-1 text-gray-500 dark:text-gray-400">
                        تومان
                    </span>
                </div>
            )
        },
        {
            title: 'جزئیات',
            dataIndex: 'id',
            key: 'id',
            render: (text, record) => (
                <Button
                    icon={<Eye size={16} />}
                    size="middle"
                    onClick={() => setOpenModal(record)}
                >
                    مشاهده
                </Button>
            )
        },
    ], []);

    return (
        <TeacherLayout
            pageTitle="زمان‌های فعالیت شما"
            backTo={URLS.teacher.dashboard}
        >
            <div className="space-y-5">
                <TimeSummary args={args} />

                {/* FILTER BOX */}
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 text-gray-800 dark:text-gray-200">
                        <Filter size={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="font-bold">
                            فیلتر نتایج
                        </span>
                    </div>

                    <div className="p-5">
                        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-5">
                            <div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                    از تاریخ
                                </div>
                                <DatePicker
                                    className="w-full h-[44px]"
                                    format="YYYY/MM/DD"
                                    value={
                                        fromDate
                                            ? dayjs(fromDate).calendar("jalali")
                                            : null
                                    }
                                    onChange={(d) => setFromDate(d ? d.calendar("gregory") : null)}
                                />
                            </div>

                            <div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                    تا تاریخ
                                </div>
                                <DatePicker
                                    className="w-full h-[44px]"
                                    format="YYYY/MM/DD"
                                    value={
                                        toDate
                                            ? dayjs(toDate).calendar("jalali")
                                            : null
                                    }
                                    onChange={(d) => setToDate(d ? d.calendar("gregory") : null)}
                                />
                            </div>

                            <div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                    انتخاب درس
                                </div>
                                <RenderLoading loading={isPendingLessonList}>
                                    <Select
                                        className="w-full"
                                        size="large"
                                        value={lesson}
                                        onChange={(e) => setLesson(e)}
                                        placeholder="انتخاب درس"
                                        allowClear
                                    >
                                        <Select.Option value={null}>
                                            همه دروس
                                        </Select.Option>

                                        {lessonList?.data?.data.map(t => (
                                            <Select.Option
                                                key={t.id + 'report-types'}
                                                value={t.id}
                                            >
                                                {toFaNumber(t.code)}
                                                {' - '}
                                                {t?.topic?.title ?? ''}
                                                {' - '}
                                                {t?.title ?? ''}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </RenderLoading>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-scroll">
                    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="font-bold text-gray-800 dark:text-gray-200">
                            لیست فعالیت‌ها
                        </div>
                    </div>

                    <div className="p-2">
                        <TableSearch
                            columns={columns}
                            fnArgs={args}
                            apiFn={useApiSearchTeacherTimes}
                        />
                    </div>
                </div>
            </div>

            {/* MODAL */}
            <Modal
                width={700}
                open={!!openModal}
                onCancel={() => setOpenModal(false)}
                footer={null}
                className="dark:[&_.ant-modal-content]:!bg-gray-900 dark:[&_.ant-modal-header]:!bg-gray-900"
            >
                <div className="pt-2">
                    <div className="flex flex-wrap items-center justify-between mb-5">
                        <div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                جزئیات فعالیت
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                اطلاعات کامل زمان ثبت شده
                            </div>
                        </div>

                        <Button
                            type="primary"
                            onClick={() => setOpenModal(false)}
                        >
                            بستن
                        </Button>
                    </div>

                    <Divider className="border-gray-200 dark:border-gray-700" />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <InfoItem title="شناسه" value={openModal?.id} />

                        <InfoItem
                            title="تاریخ"
                            value={convertToJalaliWithFormat(openModal?.date, "jYYYY/jMM/jDD")}
                        />

                        <InfoItem
                            title="مجموع زمان"
                            value={openModal?.totaltime}
                        />

                        <InfoItem
                            title="مبلغ نهایی"
                            value={
                                <>
                                    {toFaNumberFormat(openModal?.finalprice)}
                                    <span className="text-xs mr-1 text-gray-500 dark:text-gray-400">
                                        تومان
                                    </span>
                                </>
                            }
                        />

                        <InfoItem
                            title="نوع"
                            value={TeacherTimesTypeEnum(openModal?.type)}
                        />

                        <InfoItem
                            title="وضعیت"
                            value={
                                <span className={TeacherTimesStatusEnum(openModal?.status, 'color')}>
                                    {TeacherTimesStatusEnum(openModal?.status)}
                                </span>
                            }
                        />

                        <InfoItem title="مقطع" value={openModal?.grade?.title} />
                        <InfoItem title="رشته" value={openModal?.course?.title} />
                        <InfoItem title="ترم تحصیلی" value={openModal?.semester?.title} />
                        <InfoItem title="سرفصل" value={openModal?.topic?.title} />
                    </div>

                    <Divider className="border-gray-200 dark:border-gray-700" />

                    <div className="flex justify-end">
                        <Link
                            to={sprintf(URLS.teacher.lessons.detail, {
                                id: openModal?.lesson_id
                            })}
                        >
                            <Button type="dashed">
                                مشاهده جزئیات درس
                            </Button>
                        </Link>
                    </div>
                </div>
            </Modal>
        </TeacherLayout>
    );
}

function InfoItem({ title, value }) {
    return (
        <div className="rounded-xl p-4 border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/60">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {title}
            </div>
            <div className="font-bold text-gray-800 dark:text-gray-200">
                {value || '-'}
            </div>
        </div>
    );
}

export default TeacherTimesListPage;
